import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressStepNavigation from '../../components/ui/ProgressStepNavigation';
import ContactFormHeader from './components/ContactFormHeader';
import PhoneNumberInput from './components/PhoneNumberInput';
import OTPModal from './components/OTPModal';
import EmailConfirmationInput from './components/EmailConfirmationInput';
import ContactMethodSelector from './components/ContactMethodSelector';
import GDPRComplianceNotice from './components/GDPRComplianceNotice';
import FormActions from './components/FormActions';
import Icon from '../../components/AppIcon';
import axios from "axios";
import ContactDateTimePicker from "./components/ContactDateTimePicker";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ContactInformationForm = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    phoneNumber: '+33 ',
    email: '',
    contactDateTime: null
  });

  // Validation state
  const [validation, setValidation] = useState({
    phoneValid: false,
    emailValid: false,
    gdprAccepted: false,
    contactDateTime: false
  });

  // Form state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [contactDateObject, setContactDateObject] = useState(null);

  // Check if form is valid
  const isFormValid =
      validation.phoneValid &&
      validation.emailValid &&
      validation.gdprAccepted &&
      !!contactDateObject;

  // Handle phone number change
  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phoneNumber: value }));
    // Clear phone error when user starts typing
    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: '' }));
    }
  };

  const handleDateChange = (date) => {
    const formatted = formatDateTimeToString(date);
    setFormData(prev => ({
      ...prev,
      contactDateTime: formatted,
    }));
  };

  const formatDateTimeToString = (date) => {
    if (!date) return '';
    const formatted = format(date, "EEEE d MMMM 'à' HH'h'mm", { locale: fr });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  // Handle phone validation
  const handlePhoneValidation = (isValid) => {
    setValidation(prev => ({ ...prev, phoneValid: isValid }));
  };

  // Handle email change
  const handleEmailChange = (value) => {
    setFormData(prev => ({ ...prev, email: value }));
    // Clear email error when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  // Handle email validation
  const handleEmailValidation = (isValid) => {
    setValidation(prev => ({ ...prev, emailValid: isValid }));
  };

  // Handle GDPR acceptance
  const handleGDPRAcceptance = (accepted) => {
    setValidation(prev => ({ ...prev, gdprAccepted: accepted }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    try {
      const newErrors = {};

      if (!validation.phoneValid) {
        newErrors.phoneNumber = 'Veuillez entrer un numéro de téléphone valide';
      }

      if (!validation.emailValid) {
        newErrors.email = 'Veuillez entrer une adresse e-mail valide';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      let response = await axios.post(`${import.meta.env.VITE_API_BACK}/send`, {
        phoneNumber: formData.phoneNumber,
        email: formData.email,
      })
      if (response.status !== 200) {
        throw new Error('Erreur lors de l\'envoi des données');
      } else {
        setShowSuccessMessage(true);
        localStorage.setItem('contactInformation', JSON.stringify(formData));
      }



    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ 
        submit: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.' 
      });
      setIsSubmitting(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      phoneNumber: '+33 ',
      email: '',
    });
    setValidation({
      phoneValid: false,
      emailValid: true,
      gdprAccepted: false
    });
    setErrors({});
    setShowSuccessMessage(false);
  };

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('contactInformation');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Success message component
  const SuccessMessage = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-elevation-4 p-6 max-w-md w-full text-center animate-fade-in">
        <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary font-inter mb-2">
          Informations enregistrées
        </h3>
        <p className="text-text-secondary font-inter">
          Vos informations de contact ont été enregistrées avec succès.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressStepNavigation />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ContactFormHeader />

        <div className="bg-surface rounded-xl shadow-elevation-2 p-6 md:p-8">
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            {/* Phone Number Input */}
            <PhoneNumberInput
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              onValidation={handlePhoneValidation}
              error={errors.phoneNumber}
            />

            {/* Email Input */}
            <EmailConfirmationInput
              value={formData.email}
              onChange={handleEmailChange}
              onValidation={handleEmailValidation}
              error={errors.email}
            />

            <ContactDateTimePicker
                value={contactDateObject}
                onChange={(date) => {
                  setContactDateObject(date);
                  const formatted = formatDateTimeToString(date);
                  setFormData(prev => ({ ...prev, contactDateTime: formatted }));
                }}
            />

            {/* GDPR Compliance Notice */}
            <GDPRComplianceNotice
              onAcceptanceChange={handleGDPRAcceptance}
              isAccepted={validation.gdprAccepted}
            />

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                  <span className="text-sm font-medium text-error font-inter">
                    {errors.submit}
                  </span>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <FormActions
              isFormValid={isFormValid}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onReset={handleReset}
            />
          </form>
        </div>

        {/* Additional Information */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-text-secondary font-inter">
            <Icon name="Shield" size={16} color="var(--color-success)" />
            <span>
              Vos informations sont protégées par un chiffrement SSL 256 bits
            </span>
          </div>
        </div>
      </main>

      {/* Success Message Overlay */}
      {showSuccessMessage && <OTPModal formData={formData} />}
    </div>
  );
};

export default ContactInformationForm;